import { getSupabaseClient } from '../../lib/supabase'
import type { TrustTier } from './compliance-rules'

export type ComplianceWorkspace = {
  tier: TrustTier
  identity: { status: string } | null
  business: { status: string } | null
  tax: { status: string } | null
  background: { status: string } | null
  licenses: Array<{ id:string; status:string; expiration_date:string|null }>
  insurance: Array<{ id:string; status:string; expiration_date:string }>
  workersComp: Array<{ id:string; status:string }>
  categories: Array<{ id:string; name:string; eligible:boolean }>
  features: Record<string,{ enabled:boolean; adapter:string; status:string }>
}

type RpcResult<T> = PromiseLike<{ data:T|null; error:{ message:string }|null }>
function rpc<T>(name:string,args:Record<string,unknown>={}) { return getSupabaseClient().rpc(name as never,args as never) as unknown as RpcResult<T> }

export async function getComplianceWorkspace() {
  const result = await rpc<ComplianceWorkspace>('get_my_contractor_compliance')
  if (result.error) throw new Error(result.error.message)
  if (!result.data) throw new Error('Compliance workspace was not returned.')
  return result.data
}

export async function submitComplianceRecord(recordType:string,payload:Record<string,unknown>) {
  const result = await rpc<string>('submit_compliance_record',{ record_type:recordType,payload })
  if (result.error) throw new Error(result.error.message)
  return result.data
}

export async function uploadComplianceDocument(userId:string,recordType:string,file:File) {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? 'bin'
  const path = `${userId}/${recordType}/${crypto.randomUUID()}.${extension}`
  const { error } = await getSupabaseClient().storage.from('contractor-compliance').upload(path,file,{ contentType:file.type,upsert:false })
  if (error) throw error
  return path
}

export interface ComplianceProviderAdapter { readonly providerType:string; readonly configured:boolean; start():Promise<{ status:'provider_started'|'manual_review_required' }> }
export function createVendorNeutralAdapter(providerType:string,feature?:{ enabled:boolean;status:string }):ComplianceProviderAdapter {
  const configured=Boolean(feature?.enabled && feature.status==='production')
  return { providerType,configured,start(){ return Promise.resolve({ status:configured?'provider_started':'manual_review_required' }) } }
}
